import React, { useState } from "react";
import PropTypes from "prop-types";
import { Modal, Button, Input } from "rsuite";
import { toast } from "react-toastify";
import { postCorridorRequestAction } from "../../../../api";
import { getClientLogo } from "../../../../utils/clientLogos";
import TagIcon from "../../../../assets/img/action-center/tag.svg";
import ChevronRightWhiteIcon from "../../../../assets/img/action-center/chevron-right-white.svg";
import CorridorSuccessModal from "../corridor-modal/CorridorSuccessModal";
import "../corridor-modal/CorridorModal.scss";
import "./CorridorRequestModal.scss";

const FALLBACK_COLOR = "#3B82F6";

const CorridorRequestModal = ({ data, onClose, onSuccess }) => {
  const [comments, setComments] = useState("");
  const [action, setAction] = useState(null);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  if (!data) return null;

  const { recommendationId, ownerName, valueProposition } = data;
  const buyerLabel = ownerName || "Buyer";
  const buyerLogo = getClientLogo(ownerName);

  const submit = async (nextAction, comment) => {
    setAction(nextAction);
    try {
      await postCorridorRequestAction({
        recommendationId,
        action: nextAction,
        comment,
      });
      if (nextAction === "APPROVE") {
        setShowSuccessModal(true);
      } else {
        toast.success("Corridor request rejected");
        setShowReasonModal(false);
        onSuccess?.(nextAction, comment);
      }
    } catch {
      toast.error(`Failed to ${nextAction.toLowerCase()} corridor request`);
    } finally {
      setAction(null);
    }
  };

  const handleActivate = () => submit("APPROVE", "");

  const handleOpenReason = () => {
    setShowReasonModal(true);
  };

  const handleReasonBack = () => {
    if (action) return;
    setShowReasonModal(false);
    setComments("");
  };

  const handleConfirmReject = () => submit("REJECT", comments);

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    onSuccess?.("APPROVE", "");
  };

  return (
    <>
      <Modal
        className="corridor-modal corridor-request-modal"
        open
        size="sm"
        backdrop="static"
        onClose={onClose}
      >
        <Modal.Header>
          <Modal.Title>Review Buyer Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="crm-buyer-header">
            <div className="client-icon-wrapper">
              {buyerLogo ? (
                <img src={buyerLogo} alt={buyerLabel} className="client-icon" />
              ) : (
                <span
                  data-testid={`corridor-request-fallback-${recommendationId}`}
                  className="client-icon client-icon-fallback"
                  style={{ backgroundColor: FALLBACK_COLOR }}
                />
              )}
            </div>
            <div className="crm-buyer-header-text">
              <span className="crm-buyer-name">{buyerLabel}</span>
              <span className="crm-buyer-subtitle">
                Have requested to open a corridor with your hotel.
              </span>
            </div>
          </div>

          <div className="crm-request-banner">
            <span>
              This request was submitted by {buyerLabel}.{" "}
              <strong>
                Hotel Trader facilitates the connection, opting in is always
                your choice
              </strong>
              .
            </span>
          </div>

          {valueProposition && (
            <div className="crm-value-proposition">
              <span className="crm-vp-icon-wrapper">
                <img src={TagIcon} alt="" className="crm-vp-icon" />
              </span>
              <div className="crm-vp-content">
                <span className="crm-vp-title">Value Proposition</span>
                <p className="crm-vp-body">{valueProposition}</p>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance="ghost"
            className="keep-closed-btn"
            onClick={handleOpenReason}
            disabled={!!action}
          >
            Keep Closed
          </Button>
          <Button
            appearance="primary"
            className="activate-now-btn"
            onClick={handleActivate}
            disabled={!!action}
            loading={action === "APPROVE"}
          >
            Activate Now
            <img src={ChevronRightWhiteIcon} alt="" className="btn-chevron" />
          </Button>
        </Modal.Footer>
      </Modal>

      {showReasonModal && (
        <Modal
          className="corridor-modal corridor-request-reason-modal"
          open
          size="sm"
          backdrop="static"
          onClose={handleReasonBack}
        >
          <Modal.Header>
            <Modal.Title>Why keep {buyerLabel} closed?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="crm-request-banner crm-reason-banner">
              <span>
                We&apos;ll share your feedback with the buyer and update your
                closure reason.
              </span>
            </div>
            <div className="corridor-modal-comments crm-reason-comments">
              <label htmlFor="corridor-reason-comments">
                Additional comments
              </label>
              <Input
                as="textarea"
                id="corridor-reason-comments"
                rows={4}
                value={comments}
                onChange={setComments}
                placeholder="Anything else you'd like us to know?"
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              appearance="ghost"
              className="back-btn"
              onClick={handleReasonBack}
              disabled={!!action}
            >
              Back
            </Button>
            <Button
              appearance="primary"
              className="confirm-btn"
              onClick={handleConfirmReject}
              disabled={!!action}
              loading={action === "REJECT"}
            >
              Confirm
              <img src={ChevronRightWhiteIcon} alt="" className="btn-chevron" />
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {showSuccessModal && (
        <CorridorSuccessModal onClose={handleSuccessClose} />
      )}
    </>
  );
};

CorridorRequestModal.propTypes = {
  data: PropTypes.shape({
    recommendationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    ownerName: PropTypes.string,
    propertyName: PropTypes.string,
    propertyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    justificationCodes: PropTypes.arrayOf(PropTypes.string),
    valueProposition: PropTypes.string,
    expiryDate: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default CorridorRequestModal;
